/* eslint-disable react/jsx-props-no-spreading */
import type { ElementType, MutableRefObject } from "react";
import { forwardRef, useCallback, useRef } from "react";
import { IconButton, InputAdornment, SvgIcon } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import type { FieldValues, RegisterOptions, UseFormMethods } from "react-hook-form";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import ClockIcon from "@static/icons/Clock.svg";
import { cls } from "itsf-ui-common";
import { useStyle } from "./style";

type RegisterFn = UseFormMethods<FieldValues>["register"];

interface IParams {
    labelText: string;
    helperText?: string;
    icon?: ElementType;
    iconAriaLabel?: string;
    isRequired?: boolean;
    hasError?: boolean;
    isDisabled?: boolean;
    onIconClick?(): void;
    register?: RegisterFn;
    registerOptions?: RegisterOptions;
}

export const TimeTextField = forwardRef<
    HTMLDivElement,
    IParams & Omit<TextFieldProps, "label" | "aria-label" | "helperText">
>((props, ref) => {
    const {
        labelText,
        helperText,
        icon: Icon = ClockIcon,
        iconAriaLabel,
        isRequired,
        hasError,
        isDisabled,
        onIconClick,
        register,
        registerOptions,
        className,
        InputProps,
        inputRef: providedInputRef,
        type,
        ...textFieldProps
    } = props;
    const { classes } = useStyle();

    const internalInputRef = useRef<HTMLInputElement | null>(null);
    const registerInputRef = register ? register(registerOptions) : undefined;

    const setInputRef = useCallback(
        (element: HTMLInputElement | null) => {
            internalInputRef.current = element;

            if (typeof providedInputRef === "function") {
                providedInputRef(element);
            } else if (providedInputRef) {
                (providedInputRef as MutableRefObject<HTMLInputElement | null>).current = element;
            }

            if (registerInputRef) {
                registerInputRef(element);
            }
        },
        [providedInputRef, registerInputRef]
    );

    const openTimePicker = useCallback(() => {
        const input = internalInputRef.current;
        if (!input) {
            return;
        }
        if (typeof input.showPicker === "function") {
            input.showPicker();

            return;
        }
        input.focus();
    }, []);

    const handleIconClick = useCallback(() => {
        openTimePicker();
        onIconClick?.();
    }, [onIconClick, openTimePicker]);

    const endAdornment = (
        <InputAdornment position="end">
            <IconButton
                aria-label={iconAriaLabel ?? labelText}
                className={classes.iconButton}
                disabled={isDisabled}
                edge="end"
                type="button"
                onClick={handleIconClick}
            >
                <SvgIcon
                    className={cls(classes.icon, isDisabled ? classes.iconDisabled : "")}
                    component={Icon}
                    inheritViewBox
                />
            </IconButton>
        </InputAdornment>
    );

    return (
        <LabeledTextField
            {...textFieldProps}
            InputProps={{
                ...InputProps,
                endAdornment: endAdornment ?? InputProps?.endAdornment,
            }}
            className={cls(classes.root, className)}
            disabled={isDisabled}
            error={hasError}
            helperText={helperText}
            inputRef={setInputRef}
            isRequired={isRequired}
            labelText={labelText}
            ref={ref}
            type={type ?? "time"}
        />
    );
});

TimeTextField.displayName = "TimeTextField";
